<template>
    <div class="form-group">
        <label class="form-label section-title">{{ $t('messages.display_schedule_title') }}</label>
        <div class="schedule-periods">
            <div v-for="(period, index) in localData.schedule" :key="index" class="period-card">
                <div class="period-header">
                    <h3>{{ $t('messages.period') }} {{ index + 1 }}</h3>
                    <label v-if="localData.schedule && localData.schedule.length > 1" @click="deletePeriod(index)"
                        class="delete-label"
                        v-tippy="{ content: $t('messages.delete_period'), theme: 'light' }"></label>
                </div>

                <div class="period-fields">
                    <div class="field-group">
                        <label class="form-label">{{ $t('messages.start_date') }}</label>
                        <div class="datetime-input">
                            <div class="date-input-group">
                                <input type="date" class="form-input" v-model="period.startDate"
                                    :min="getMinStartDate(index)" placeholder="DD.MM.YYYY"
                                    @change="onStartDateChange(index)" />
                                <img src="/images/calendar-days.svg" alt="calendar" class="input-icon" />
                            </div>
                            <div class="time-input-group">
                                <input type="time" class="form-input" v-model="period.startTime" @change="updateData" />
                                <img src="/images/clock4.svg" alt="time" class="input-icon" />
                            </div>
                        </div>
                    </div>

                    <div class="field-group">
                        <label class="form-label">{{ $t('messages.end_date') }}</label>
                        <div class="datetime-input">
                            <div class="date-input-group">
                                <input type="date" class="form-input" v-model="period.endDate"
                                    :min="period.startDate || getTodayDate()" placeholder="DD.MM.YYYY"
                                    @change="onEndDateChange(index)" />
                                <img src="/images/calendar-days.svg" alt="calendar" class="input-icon" />
                            </div>
                            <div class="time-input-group">
                                <input type="time" class="form-input" v-model="period.endTime" @change="updateData" />
                                <img src="/images/clock4.svg" alt="time" class="input-icon" />
                            </div>
                        </div>
                    </div>

                    <div class="field-group">
                        <label class="form-label">{{ $t('messages.budget') }}</label>
                        <div class="value-input-group">
                            <input type="number" class="form-input" v-model="period.budget"
                                @input="updatePeriodBudget(index)" step="0.01" min="0"
                                :readonly="isTest" />
                            <img :src="getCurrencyIcon(currency)" :alt="currency" class="currency-icon" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="schedule-summary">
                <span class="total-text">
                    {{ $t('messages.total_budget_text') }}
                    {{ (localData.totalBudget || 0).toFixed(2) }} {{ $t('messages.currency_symbols.' + currency) }}
                </span>
                <button class="btn-add" @click="addPeriod">
                    <span class="add-icon">+</span> {{ $t('messages.add') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "SchedulePeriods",
    props: {
        // initialSchedules: array of schedule objects from the server (relation) used only to initialize
        initialSchedules: {
            type: Array,
            default: () => []
        },
        // campaign id for informational purposes
        campaignId: {
            type: [String, Number],
            default: null
        },
        currency: {
            type: String,
            default: 'USD'
        },
        currencies: {
            type: Array,
            default: () => []
        },
        isTest: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            localData: {
                schedule: [],
                totalBudget: 0
            },
            isDirty: false
        };
    },
    watch: {
        initialSchedules: {
            handler(newVal) {
                // Apply initial schedules only if we don't have local schedule data yet
                if (!this.isDirty && (!this.localData.schedule || this.localData.schedule.length === 0)) {
                    this.applyInitialSchedules(newVal);
                } else {
                }
            },
            immediate: true,
            deep: true
        },
        isTest: {
            handler(value) {
                if (!value || !Array.isArray(this.localData.schedule)) {
                    return;
                }
                this.localData.schedule.forEach((period) => {
                    period.budget = 0;
                });
                this.calculateTotalScheduleBudget();
                this.isDirty = true;
                this.updateData();
            }
        }
    },
    mounted() {
        if (!this.isDirty) this.applyInitialSchedules(this.initialSchedules);
    },
    methods: {
        updatePeriodBudget(index) {
            this.calculateTotalScheduleBudget();
            this.isDirty = true;
            this.updateData();
        },

        calculateTotalScheduleBudget() {
            if (!this.localData.schedule || !Array.isArray(this.localData.schedule)) {
                this.localData.totalBudget = 0;
                return;
            }
            this.localData.totalBudget = this.localData.schedule.reduce((total, period) => {
                return total + (parseFloat(period.budget) || 0);
            }, 0);
        },

        deletePeriod(index) {
            if (this.localData.schedule && this.localData.schedule.length > 1) {
                this.localData.schedule.splice(index, 1);
                this.calculateTotalScheduleBudget();
                this.isDirty = true;
                this.updateData();
            }
        },

        addPeriod() {
            if (!this.localData.schedule) {
                this.localData.schedule = [];
            }
            const today = this.getTodayDate();
            let startDate = today;
            if (this.localData.schedule && this.localData.schedule.length) {
                const last = this.localData.schedule[this.localData.schedule.length - 1];
                if (last && last.endDate) {
                    const d = new Date(last.endDate);
                    d.setDate(d.getDate() + 1);
                    startDate = d.toISOString().split('T')[0];
                }
            }
            const newPeriod = {
                startDate: startDate,
                startTime: '00:00',
                endDate: startDate,
                endTime: '23:59',
                budget: 0
            };
            this.localData.schedule.push(newPeriod);
            this.isDirty = true;
            this.updateData();
        },

        onStartDateChange(periodIndex) {
            if (!this.localData.schedule || !this.localData.schedule[periodIndex]) {
                return;
            }
            const period = this.localData.schedule[periodIndex];

            // Enforce start date minimums relative to previous period
            if (periodIndex > 0) {
                const prev = this.localData.schedule[periodIndex - 1];
                if (prev && prev.endDate) {
                    const minStart = this.addDays(prev.endDate, 1);
                    if (period.startDate <= prev.endDate) {
                        period.startDate = minStart;
                    }
                }
            } else {
                // first period cannot be before today
                const today = this.getTodayDate();
                if (period.startDate < today) {
                    period.startDate = today;
                }
            }

            // If endDate is earlier than startDate, set it to startDate
            if (period.endDate && period.endDate < period.startDate) {
                period.endDate = period.startDate;
            }

            this.isDirty = true;
            this.updateData();
        },

        onEndDateChange(periodIndex) {
            if (!this.localData.schedule || !this.localData.schedule[periodIndex]) return;
            const period = this.localData.schedule[periodIndex];
            // ensure endDate >= startDate
            if (period.endDate && period.startDate && period.endDate < period.startDate) {
                period.endDate = period.startDate;
            }
            // also ensure next period (if exists) starts after this endDate
            if (this.localData.schedule[periodIndex + 1]) {
                const next = this.localData.schedule[periodIndex + 1];
                const minNextStart = this.addDays(period.endDate, 1);
                if (next.startDate && next.startDate <= period.endDate) {
                    next.startDate = minNextStart;
                    if (next.endDate && next.endDate < next.startDate) {
                        next.endDate = next.startDate;
                    }
                }
            }
            this.isDirty = true;
            this.updateData();
        },


        getTodayDate() {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        ensureValidDates() {
            if (!this.localData.schedule || !Array.isArray(this.localData.schedule)) {
                return;
            }

            const today = this.getTodayDate();
            this.localData.schedule.forEach(period => {
                // Если startDate пустой, устанавливаем сегодняшнюю дату
                if (!period.startDate) {
                    period.startDate = today;
                }
                // Если endDate пустой, устанавливаем его равным startDate
                if (!period.endDate) {
                    period.endDate = period.startDate || today;
                }
                // Если endDate меньше startDate, устанавливаем его равным startDate
                if (period.startDate && period.endDate && period.endDate < period.startDate) {
                    period.endDate = period.startDate;
                }
            });
            // enforce non-overlapping: each start must be > previous end
            for (let i = 1; i < this.localData.schedule.length; i++) {
                const prev = this.localData.schedule[i - 1];
                const cur = this.localData.schedule[i];
                if (prev && prev.endDate) {
                    const minStart = this.addDays(prev.endDate, 1);
                    if (cur.startDate <= prev.endDate) {
                        cur.startDate = minStart;
                        if (!cur.endDate || cur.endDate < cur.startDate) {
                            cur.endDate = cur.startDate;
                        }
                    }
                }
            }
        },

        getMinStartDate(index) {
            if (index === 0) return this.getTodayDate();
            const prev = this.localData.schedule && this.localData.schedule[index - 1];
            if (prev && prev.endDate) {
                return this.addDays(prev.endDate, 1);
            }
            return this.getTodayDate();
        },

        addDays(dateString, days) {
            if (!dateString) return this.getTodayDate();
            const d = new Date(dateString);
            d.setDate(d.getDate() + days);
            return d.toISOString().split('T')[0];
        },


        updateData() {
            this.ensureValidDates();
            this.$emit('update', this.localData);
        },

        applyInitialSchedules(schedules) {
            if (!schedules || !Array.isArray(schedules) || schedules.length === 0) {
                this.localData.schedule = [{
                    startDate: this.getTodayDate(),
                    startTime: '00:00',
                    endDate: this.getTodayDate(),
                    endTime: '23:59',
                    budget: 0
                }];
                this.calculateTotalScheduleBudget();
                return;
            }

            const mapped = schedules.slice().sort((a, b) => {
                const da = a.start_date ? a.start_date.split('T')[0] : a.startDate || '';
                const db = b.start_date ? b.start_date.split('T')[0] : b.startDate || '';
                if (da < db) return -1;
                if (da > db) return 1;
                return 0;
            }).map(s => {
                const sd = s.start_date ?? s.startDate ?? null;
                const st = s.start_time ?? s.startTime ?? null;
                const ed = s.end_date ?? s.endDate ?? null;
                const et = s.end_time ?? s.endTime ?? null;
                const b = (s.budget !== undefined && s.budget !== null) ? s.budget : (s.budget === 0 ? 0 : null);
                return {
                    startDate: sd ? (typeof sd === 'string' && sd.includes('T') ? sd.split('T')[0] : sd) : this.getTodayDate(),
                    startTime: st || '00:00',
                    endDate: ed ? (typeof ed === 'string' && ed.includes('T') ? ed.split('T')[0] : ed) : (sd ? (typeof sd === 'string' && sd.includes('T') ? sd.split('T')[0] : sd) : this.getTodayDate()),
                    endTime: et || '23:59',
                    budget: b !== null ? parseFloat(b) : 0
                };
            });

            this.localData.schedule = mapped;
            this.calculateTotalScheduleBudget();
            this.ensureValidDates();
        },

        getCurrencyIcon(currency) {
            const currencyData = this.currencies.find(c => c.currency === currency);
            return currencyData?.icon || '/images/dollar.svg';
        }
    }
};
</script>

<style lang="scss" scoped>
.schedule-periods {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.period-card {
    padding: 24px;
    border-radius: 2px;
    border: 1px solid #e9ecef;

    .period-header {
        margin-bottom: 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        h3 {
            font-size: 18px;
            margin: 0;
        }
    }

    .period-fields {
        display: flex;
        flex-direction: column;
        gap: 20px;

        .field-group {
            display: flex;
            flex-direction: column;
            gap: 8px;

            .form-label {
                margin-bottom: 4px;
            }
        }

        .datetime-input {
            display: flex;
            gap: 8px;

            .date-input-group,
            .time-input-group {
                position: relative;
                flex: 1;

                .form-input {

                    // Стили для нативных элементов даты и времени
                    &[type="date"],
                    &[type="time"] {
                        color-scheme: light;

                        // Скрываем нативные иконки браузера
                        &::-webkit-calendar-picker-indicator {
                            opacity: 0;
                            position: absolute;
                            right: 0;
                            width: 100%;
                            height: 100%;
                            cursor: pointer;
                        }

                        &::-webkit-inner-spin-button,
                        &::-webkit-outer-spin-button {
                            -webkit-appearance: none;
                            margin: 0;
                        }
                    }
                }

                .input-icon {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 16px;
                    height: 16px;
                    filter: brightness(0) saturate(100%) invert(44%) sepia(8%) saturate(1038%) hue-rotate(169deg) brightness(95%) contrast(86%);
                    pointer-events: none;
                    z-index: 1;
                }
            }
        }

        .value-input-group {

            .currency-icon {
                position: absolute;
                right: 12px;
                top: 50%;
                transform: translateY(-50%);
                width: 16px;
                height: 16px;
                filter: brightness(0) saturate(100%) invert(44%) sepia(8%) saturate(1038%) hue-rotate(169deg) brightness(95%) contrast(86%);
                pointer-events: none;
                z-index: 10;
            }
        }
    }
}

.schedule-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 2px;
}
</style>
